<template>
  <div class="discounts-page">
    <header class="discounts-page__header">
      <div>
        <h1>{{ $t('catalog.discounts.title') }}</h1>
        <p>{{ $t('catalog.discounts.subtitle') }}</p>
      </div>
      <button type="button" class="discounts-page__btn" @click="openCreate">
        <i class="mdi mdi-plus"></i>
        {{ $t('catalog.discounts.add') }}
      </button>
    </header>

    <div v-if="pagedDiscounts.length" class="discounts-page__grid">
      <article
        v-for="discount in pagedDiscounts"
        :key="discount.id"
        class="discount-card"
        :class="{ 'discount-card--off': !discount.is_active }"
      >
        <div class="discount-card__top">
          <div class="discount-card__icon">
            <i :class="['mdi', discount.type === 'percent' ? 'mdi-percent-outline' : 'mdi-cash-minus']"></i>
          </div>
          <div class="discount-card__actions">
            <button
              type="button"
              class="discount-card__icon-btn"
              :title="discount.is_active ? $t('catalog.discounts.turnOff') : $t('catalog.discounts.turnOn')"
              @click="toggleActive(discount)"
            >
              <i :class="['mdi', discount.is_active ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline']"></i>
            </button>
            <button
              type="button"
              class="discount-card__icon-btn"
              :title="$t('catalog.common.edit')"
              @click="openEdit(discount)"
            >
              <i class="mdi mdi-pencil-outline"></i>
            </button>
            <button
              type="button"
              class="discount-card__icon-btn discount-card__icon-btn--danger"
              :title="$t('catalog.common.delete')"
              @click="askDelete(discount)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
          </div>
        </div>
        <h3>{{ discount.name }}</h3>
        <div class="discount-card__value">
          <strong>{{ discountLabel(discount) }}</strong>
          <span v-if="!discount.is_active" class="discount-card__status">{{ $t('catalog.discounts.inactive') }}</span>
        </div>
      </article>
    </div>

    <div v-else class="discounts-page__empty">
      <i class="mdi mdi-sale-outline"></i>
      <p>{{ $t('catalog.discounts.empty') }}</p>
      <span>{{ $t('catalog.discounts.emptyHint') }}</span>
    </div>

    <div v-if="pageCount > 1" class="discounts-page__pagination">
      <v-pagination v-model="page" :length="pageCount" :total-visible="5" color="#0b4f8a" />
    </div>

    <v-dialog v-model="formOpen" max-width="480">
      <v-card class="discounts-page__dialog">
        <v-card-title>{{ isEdit ? $t('catalog.discounts.editTitle') : $t('catalog.discounts.createTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" :label="$t('catalog.common.name')" outlined dense class="mb-2" :placeholder="$t('catalog.discounts.namePlaceholder')" />
          <v-btn-toggle v-model="form.type" mandatory dense class="mb-4 discounts-page__toggle">
            <v-btn value="amount" text>{{ $t('catalog.discounts.amount') }}</v-btn>
            <v-btn value="percent" text>{{ $t('catalog.discounts.percent') }}</v-btn>
          </v-btn-toggle>
          <v-text-field
            v-model="form.value"
            :label="form.type === 'percent' ? $t('catalog.discounts.discountPercent') : $t('catalog.discounts.discountAmount')"
            :suffix="form.type === 'percent' ? '%' : '₸'"
            outlined
            dense
            type="number"
            min="0"
          />
          <p class="discounts-page__hint">
            {{ form.type === 'percent'
              ? $t('catalog.discounts.percentExample')
              : $t('catalog.discounts.amountExample') }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="formOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="saving" @click="save">{{ $t('catalog.common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteOpen" max-width="420">
      <v-card class="discounts-page__dialog">
        <v-card-title>{{ $t('catalog.discounts.deleteTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('catalog.discounts.deleteDescription', { name: deleteName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">{{ $t('catalog.common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  createDiscount,
  deleteDiscount,
  getDiscounts,
  updateDiscount,
} from '@/api/discounts'
import { notify, notifyError } from '@/utils/notify'

const PER_PAGE = 12

export default {
  name: 'DiscountControl',
  data() {
    return {
      discounts: [],
      page: 1,
      formOpen: false,
      isEdit: false,
      discountId: null,
      form: {
        name: '',
        type: 'amount',
        value: '',
      },
      saving: false,
      deleteOpen: false,
      deleteName: '',
      deleting: false,
    }
  },
  computed: {
    pageCount() {
      return Math.ceil(this.discounts.length / PER_PAGE) || 0
    },
    pagedDiscounts() {
      const start = (this.page - 1) * PER_PAGE
      return this.discounts.slice(start, start + PER_PAGE)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    discountLabel(discount) {
      const value = Number(discount.value) || 0
      return discount.type === 'percent'
        ? `−${value}%`
        : `−${new Intl.NumberFormat('ru-RU').format(value)} ₸`
    },
    async load() {
      try {
        this.discounts = await getDiscounts()
        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      }
    },
    openCreate() {
      this.isEdit = false
      this.discountId = null
      this.form = { name: '', type: 'amount', value: '' }
      this.formOpen = true
    },
    openEdit(discount) {
      this.isEdit = true
      this.discountId = discount.id
      this.form = {
        name: discount.name,
        type: discount.type,
        value: discount.value,
      }
      this.formOpen = true
    },
    askDelete(discount) {
      this.discountId = discount.id
      this.deleteName = discount.name
      this.deleteOpen = true
    },
    async toggleActive(discount) {
      try {
        await updateDiscount(discount.id, {
          name: discount.name,
          type: discount.type,
          value: discount.value,
          is_active: !discount.is_active,
        })
        notify(discount.is_active ? this.$t('catalog.discounts.turnedOff') : this.$t('catalog.discounts.turnedOn'))
        await this.load()
      } catch (error) {
        notifyError(error)
      }
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await deleteDiscount(this.discountId)
        notify(this.$t('catalog.discounts.deleted'), 'warning')
        this.deleteOpen = false
        await this.load()
      } catch (error) {
        notifyError(error, this.$t('catalog.discounts.deleteFailed'))
      } finally {
        this.deleting = false
      }
    },
    async save() {
      if (!this.form.name) {
        notify(this.$t('catalog.categories.enterName'), 'warning')
        return
      }
      const value = Number(this.form.value)
      if (!value || value <= 0) {
        notify(this.$t('catalog.discounts.enterValue'), 'warning')
        return
      }
      if (this.form.type === 'percent' && value >= 100) {
        notify(this.$t('catalog.discounts.invalidPercent'), 'warning')
        return
      }

      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          type: this.form.type,
          value,
        }
        const result = this.isEdit
          ? await updateDiscount(this.discountId, payload)
          : await createDiscount(payload)

        notify(result.message || this.$t('catalog.common.saved'))
        this.formOpen = false
        await this.load()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.discounts-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.discounts-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.discounts-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.2s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #093f6d;
  }
}

.discounts-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.discount-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  transition: border-color 0.2s ease, transform 0.15s ease, opacity 0.2s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-2px);
  }

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
  }

  &--off {
    opacity: 0.55;
  }
}

.discount-card__value {
  display: flex;
  align-items: center;
  gap: 10px;

  strong {
    font-size: 20px;
    font-weight: 750;
    color: #0b4f8a;
  }
}

.discount-card__status {
  font-size: 12px;
  color: #b42318;
  font-weight: 650;
}

.discount-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.discount-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }
}

.discount-card__actions {
  display: flex;
  gap: 6px;
}

.discount-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  background: #fff;
  color: #3d4f63;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    border-color: #0b4f8a;
    color: #0b4f8a;
    background: #eef4fa;
  }

  &--danger {
    color: #b42318;

    &:hover {
      border-color: #f0b4ae;
      color: #b42318;
      background: #fff5f4;
    }
  }
}

.discounts-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: #152033;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.discounts-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.discounts-page__dialog {
  border-radius: 16px !important;
}

.discounts-page__toggle {
  width: 100%;

  .v-btn {
    flex: 1;
  }
}

.discounts-page__hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7a90;
}

@media (max-width: 720px) {
  .discounts-page {
    padding: 20px 16px 32px;
  }

  .discounts-page__header {
    flex-direction: column;
  }
}
</style>
